import type { TemplateRegistration, TemplateData } from './types';
import { sanitizeText, sanitizeSeoText, escapeAttr } from './sanitize';
import { getIconSvg, renderStars, buildFontsUrl, buildNavLinks, NavDataFlags, truncateAtSentence } from './helpers';

export const timberLodge: TemplateRegistration = {
  meta: {
    id: 'timber-lodge',
    name: 'Timber Lodge',
    category: 'Classic',
    description: 'Warm wood tones with overlapping card sections',
    previewImage: '',
    supportedFeatures: ['contact-form', 'phone-display', 'address-map', 'business-hours', 'about-section', 'services-list', 'testimonials', 'google-reviews', 'social-links', 'faq', 'price-list', 'promotions', 'photo-gallery'],
    defaultEnabledFeatures: ['contact-form', 'phone-display', 'about-section', 'services-list', 'testimonials'],
  },
  render(data: TemplateData, enabledFeatures: Set<string>): string {
    const safeName = sanitizeText(data.businessName);
    const safeHeadline = sanitizeText(data.headline);
    const safeAbout = sanitizeText(data.aboutText);
    const safeSeoTitle = sanitizeSeoText(data.seo.title, 70);
    const safeSeoDesc = sanitizeSeoText(data.seo.description, 160);

    const phone = data.businessData?.phone || null;
    const contactEmail = data.businessData?.contactEmail || null;
    const rating = data.businessData?.rating || null;
    const reviewCount = data.businessData?.reviewCount || 0;
    const businessHours = data.businessData?.businessHours || [];
    const photos = data.businessData?.photos || [];
    const address = data.businessData?.address || '';
    const googleMapsUrl = data.businessData?.googleMapsUrl || null;
    const heroImage = photos[0] || null;
    const galleryImages = photos.slice(1, 6);

    const navDataFlags: NavDataFlags = {
      hasServices: data.services.length > 0,
      hasTestimonials: data.testimonials.length > 0,
      hasGalleryPhotos: galleryImages.length > 0,
      hasFaq: !!(data.faq && data.faq.length > 0),
      hasPriceList: !!(data.priceList && data.priceList.length > 0),
    };
    const navLinks = buildNavLinks(enabledFeatures, navDataFlags);
    const fontsUrl = buildFontsUrl(['Playfair Display', 'Rubik']);

    const sections: string[] = [];

    // --- Google Reviews ---
    if (enabledFeatures.has('google-reviews') && rating) {
      sections.push(`
      <section class="google-reviews">
        <div class="container">
          <div class="review-badge">
            <div class="review-stars">${renderStars(Math.round(rating))}</div>
            <span class="review-text">${rating.toFixed(1)} out of 5 &mdash; ${reviewCount} Google reviews</span>
          </div>
        </div>
      </section>`);
    }

    // --- Promotions ---
    if (enabledFeatures.has('promotions') && data.promotion) {
      const promoTitle = sanitizeText(data.promotion.title);
      const promoDesc = sanitizeText(data.promotion.description);
      const promoDiscount = sanitizeText(data.promotion.discount);
      sections.push(`
      <section class="promo-banner">
        <div class="container">
          <div class="promo-inner">
            <span class="promo-badge">${promoDiscount}</span>
            <div class="promo-content">
              <strong>${promoTitle}</strong>
              <span>${promoDesc}</span>
            </div>
          </div>
        </div>
      </section>`);
    }

    // --- About ---
    if (enabledFeatures.has('about-section')) {
      const aboutQuote = truncateAtSentence(safeAbout, 120);
      sections.push(`
      <section id="about" class="section section-cream">
        <div class="container">
          <div class="timber-rule"></div>
          <h2 class="section-title">About Us</h2>
          <div class="about-grid">
            <blockquote class="about-quote">&ldquo;${aboutQuote}&rdquo;</blockquote>
            <p class="about-text">${safeAbout}</p>
          </div>
        </div>
      </section>`);
    }

    // --- Services ---
    if (enabledFeatures.has('services-list') && data.services.length > 0) {
      const serviceCards = data.services.map((s) => {
        const sName = sanitizeText(s.name);
        const sDesc = sanitizeText(s.description);
        return `
          <div class="service-card">
            <div class="service-icon">${getIconSvg(s.icon || 'star')}</div>
            <h3>${sName}</h3>
            <p>${sDesc}</p>
            ${s.price ? `<span class="service-price">${sanitizeText(s.price)}</span>` : ''}
          </div>`;
      }).join('');
      sections.push(`
      <section id="services" class="section section-alt">
        <div class="container">
          <h2 class="section-title">Our Services</h2>
          <div class="services-masonry">${serviceCards}</div>
        </div>
      </section>`);
    }

    // --- Photo Gallery ---
    if (enabledFeatures.has('photo-gallery') && galleryImages.length > 0) {
      const imgs = galleryImages.map((url, i) => `<div class="gallery-frame${i > 0 ? ' frame-overlap' : ''}"><img src="${escapeAttr(url)}" alt="${safeName}" loading="lazy"></div>`).join('');
      sections.push(`
      <section id="gallery" class="section section-cream">
        <div class="container">
          <h2 class="section-title">Gallery</h2>
          <div class="gallery-grid">${imgs}</div>
        </div>
      </section>`);
    }

    // --- Testimonials ---
    if (enabledFeatures.has('testimonials') && data.testimonials.length > 0) {
      const cards = data.testimonials.map(t => {
        const tName = sanitizeText(t.name);
        const tContent = sanitizeText(t.content);
        const tRole = t.role ? sanitizeText(t.role) : '';
        return `
          <div class="testimonial-card">
            <div class="testimonial-body">
              <p>&ldquo;${tContent}&rdquo;</p>
            </div>
            <div class="testimonial-author">
              <strong>${tName}</strong>
              ${tRole ? `<span>${tRole}</span>` : ''}
              ${t.rating ? `<div class="testimonial-stars">${renderStars(t.rating)}</div>` : ''}
            </div>
          </div>`;
      }).join('');
      sections.push(`
      <section id="testimonials" class="section section-alt">
        <div class="container">
          <h2 class="section-title">What People Say</h2>
          <div class="testimonials-carousel">${cards}</div>
        </div>
      </section>`);
    }

    // --- FAQ ---
    if (enabledFeatures.has('faq') && data.faq && data.faq.length > 0) {
      const items = data.faq.map(f => {
        const q = sanitizeText(f.question);
        const a = sanitizeText(f.answer);
        return `
          <div class="faq-item">
            <h3 class="faq-q">${q}</h3>
            <p class="faq-a">${a}</p>
          </div>`;
      }).join('');
      sections.push(`
      <section id="faq" class="section section-cream">
        <div class="container">
          <h2 class="section-title">FAQ</h2>
          <div class="faq-list">${items}</div>
        </div>
      </section>`);
    }

    // --- Price List ---
    if (enabledFeatures.has('price-list') && data.priceList && data.priceList.length > 0) {
      const rows = data.priceList.map(p => {
        const pName = sanitizeText(p.name);
        const pDesc = sanitizeText(p.description);
        const pPrice = sanitizeText(p.price);
        return `
          <div class="price-row">
            <div class="price-info">
              <h3>${pName}</h3>
              <p>${pDesc}</p>
            </div>
            <span class="price-amount">${pPrice}</span>
          </div>`;
      }).join('');
      sections.push(`
      <section id="pricing" class="section section-alt">
        <div class="container">
          <h2 class="section-title">Pricing</h2>
          <div class="price-list">${rows}</div>
        </div>
      </section>`);
    }

    // --- Business Hours ---
    if (enabledFeatures.has('business-hours') && businessHours.length > 0) {
      const hoursList = businessHours.map(h => `<li>${sanitizeText(h)}</li>`).join('');
      sections.push(`
      <section class="section section-cream">
        <div class="container">
          <h2 class="section-title">Hours</h2>
          <ul class="hours-list">${hoursList}</ul>
        </div>
      </section>`);
    }

    // --- Contact ---
    if (enabledFeatures.has('contact-form')) {
      sections.push(`
      <section id="contact" class="section section-alt">
        <div class="container">
          <h2 class="section-title">Get In Touch</h2>
          <div class="contact-split">
            <div class="contact-accent"></div>
            <div class="contact-info">
              ${enabledFeatures.has('phone-display') && phone ? `<div class="contact-item">${getIconSvg('phone')}<a href="tel:${escapeAttr(phone)}">${sanitizeText(phone)}</a></div>` : ''}
              ${enabledFeatures.has('address-map') && address ? `<div class="contact-item">${getIconSvg('map-pin')}<span>${sanitizeText(address)}</span></div>` : ''}
              ${enabledFeatures.has('address-map') && googleMapsUrl ? `<a class="map-link" href="${escapeAttr(googleMapsUrl)}" target="_blank" rel="noopener noreferrer">View on Google Maps</a>` : ''}
            </div>
            <form class="contact-form" ${contactEmail ? `action="https://formsubmit.co/${escapeAttr(contactEmail)}" method="POST"` : 'onsubmit="return false;"'}>
              <input type="text" placeholder="Your Name" required>
              <input type="email" placeholder="Your Email" required>
              <textarea placeholder="Message" rows="4" required></textarea>
              <button type="submit">Send Message</button>
            </form>
          </div>
        </div>
      </section>`);
    }

    return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${safeSeoTitle}</title>
  <meta name="description" content="${safeSeoDesc}">
  <link rel="preconnect" href="https://fonts.googleapis.com">
  <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
  <link href="${fontsUrl}" rel="stylesheet">
  <style>
    *, *::before, *::after { margin: 0; padding: 0; box-sizing: border-box; }
    :root {
      --primary: #6B4226; --bg: #FFF8E7; --bg-alt: #F0E4CC; --accent: #6B4226;
      --text: #2D1B0E; --text-muted: #7C6354;
      --font-heading: 'Playfair Display', serif; --font-body: 'Rubik', sans-serif;
    }
    html { scroll-behavior: smooth; }
    body { font-family: var(--font-body); background: var(--bg); color: var(--text); line-height: 1.6; }
    .container { max-width: 1000px; margin: 0 auto; padding: 0 24px; }
    a { color: var(--accent); text-decoration: none; transition: opacity .2s; }
    a:hover { opacity: .8; }

    /* NAV */
    .navbar { position: fixed; top: 0; left: 0; right: 0; z-index: 100; background: rgba(255,248,231,.95); backdrop-filter: blur(8px); border-bottom: 4px solid var(--accent); }
    .nav-inner { display: flex; align-items: center; justify-content: space-between; padding: 16px 24px; max-width: 1000px; margin: 0 auto; }
    .nav-brand { font-family: var(--font-heading); font-size: 1.5rem; color: var(--text); font-weight: 700; }
    .nav-links { display: flex; gap: 24px; list-style: none; }
    .nav-links a { color: var(--text-muted); font-size: .85rem; text-transform: uppercase; letter-spacing: 1.5px; font-weight: 500; }
    .nav-links a:hover { color: var(--accent); }
    .nav-toggle { display: none; background: none; border: none; cursor: pointer; }
    .nav-toggle span { display: block; width: 26px; height: 3px; background: var(--accent); margin: 5px 0; transition: .3s; border-radius: 2px; }

    /* TIMBER RULE */
    .timber-rule { width: 100px; height: 6px; background: linear-gradient(90deg, #6B4226, #8B6914, #6B4226); border-radius: 3px; margin: 0 auto 24px; }

    /* HERO - banner with side accent bar */
    .hero { position: relative; min-height: 100vh; display: flex; align-items: center; overflow: hidden; }
    .hero-bg { position: absolute; inset: 0; background-size: cover; background-position: center; opacity: .25; }
    .hero-overlay { position: absolute; inset: 0; background: linear-gradient(to bottom, rgba(255,248,231,.5), var(--bg)); }
    .hero-accent-bar { position: absolute; top: 0; left: 0; bottom: 0; width: 60px; background: var(--accent); z-index: 3; }
    .hero-content { position: relative; z-index: 2; padding: 120px 24px 80px 100px; max-width: 1000px; margin: 0 auto; width: 100%; }
    .hero h1 { font-family: var(--font-heading); font-size: clamp(2.5rem, 7vw, 4.5rem); line-height: 1.1; font-weight: 700; margin-bottom: 16px; color: var(--text); }
    .hero-sub { font-size: clamp(1rem, 2vw, 1.15rem); color: var(--text-muted); max-width: 600px; margin-bottom: 32px; }
    .hero-cta { display: inline-block; padding: 16px 40px; background: var(--accent); color: var(--bg); font-family: var(--font-heading); font-size: 1.05rem; letter-spacing: 1px; border: none; border-radius: 8px; cursor: pointer; transition: transform .2s, box-shadow .2s; }
    .hero-cta:hover { transform: translateY(-2px); box-shadow: 0 8px 24px rgba(107,66,38,.3); opacity: 1; color: var(--bg); }

    /* SECTIONS */
    .section { padding: 80px 0; }
    .section-cream { background: var(--bg); }
    .section-alt { background: var(--bg-alt); }
    .section-title { font-family: var(--font-heading); font-size: clamp(1.8rem, 5vw, 2.8rem); text-align: center; margin-bottom: 48px; color: var(--text); }

    /* ABOUT - quote + text side-by-side */
    .about-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 48px; align-items: start; }
    .about-quote { font-family: var(--font-heading); font-size: 1.6rem; font-style: italic; color: var(--accent); line-height: 1.5; border-left: 4px solid var(--accent); padding-left: 24px; }
    .about-text { font-size: 1.05rem; color: var(--text-muted); line-height: 1.8; }

    /* SERVICES - masonry (CSS columns) */
    .services-masonry { column-count: 2; column-gap: 24px; }
    .service-card { background: white; border: 3px solid var(--accent); border-radius: 12px; padding: 32px; margin-bottom: 24px; break-inside: avoid; box-shadow: 4px 4px 0 rgba(107,66,38,.12); transition: transform .2s; }
    .service-card:hover { transform: translateY(-4px); }
    .service-icon { color: var(--accent); margin-bottom: 12px; }
    .icon-char { font-size: 26px; line-height: 1; }
    .service-card h3 { font-family: var(--font-heading); font-size: 1.3rem; margin-bottom: 8px; }
    .service-card p { color: var(--text-muted); font-size: .95rem; }
    .service-price { display: inline-block; margin-top: 12px; color: var(--accent); font-weight: 700; font-size: 1.05rem; }

    /* GALLERY - OVERLAPPING FRAMES */
    .gallery-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; }
    .gallery-frame { border: 4px solid var(--accent); border-radius: 8px; overflow: hidden; box-shadow: 4px 4px 0 rgba(107,66,38,.15); transition: transform .2s; }
    .gallery-frame.frame-overlap { margin-top: -16px; }
    .gallery-frame:hover { transform: translateY(-4px); }
    .gallery-frame img { width: 100%; height: 220px; object-fit: cover; display: block; }

    /* TESTIMONIALS - scroll-snap horizontal carousel */
    .testimonials-carousel { display: flex; gap: 24px; overflow-x: auto; scroll-snap-type: x mandatory; -webkit-overflow-scrolling: touch; padding-bottom: 16px; }
    .testimonials-carousel::-webkit-scrollbar { height: 6px; }
    .testimonials-carousel::-webkit-scrollbar-track { background: rgba(107,66,38,.1); border-radius: 3px; }
    .testimonials-carousel::-webkit-scrollbar-thumb { background: var(--accent); border-radius: 3px; }
    .testimonial-card { background: white; border: 3px solid var(--accent); border-radius: 12px; padding: 32px; box-shadow: 4px 4px 0 rgba(107,66,38,.12); min-width: 320px; max-width: 400px; flex-shrink: 0; scroll-snap-align: start; }
    .testimonial-body p { font-size: 1.05rem; font-style: italic; color: var(--text-muted); line-height: 1.7; margin-bottom: 20px; }
    .testimonial-author strong { display: block; color: var(--text); font-size: 1rem; }
    .testimonial-author span { color: var(--text-muted); font-size: .85rem; }
    .testimonial-stars { margin-top: 8px; }
    .star { color: #C4B09E; font-size: 1rem; }
    .star.filled { color: var(--accent); }

    /* FAQ */
    .faq-list { max-width: 800px; margin: 0 auto; }
    .faq-item { border-bottom: 2px solid rgba(107,66,38,.15); padding: 24px 0; }
    .faq-q { font-family: var(--font-heading); font-size: 1.2rem; margin-bottom: 10px; }
    .faq-a { color: var(--text-muted); line-height: 1.7; }

    /* PRICE LIST */
    .price-list { max-width: 800px; margin: 0 auto; }
    .price-row { display: flex; justify-content: space-between; align-items: flex-start; padding: 24px 0; border-bottom: 2px solid rgba(107,66,38,.15); gap: 24px; }
    .price-info h3 { font-family: var(--font-heading); font-size: 1.1rem; margin-bottom: 4px; }
    .price-info p { color: var(--text-muted); font-size: .9rem; }
    .price-amount { font-family: var(--font-heading); font-size: 1.4rem; color: var(--accent); white-space: nowrap; font-weight: 700; }

    /* HOURS */
    .hours-list { list-style: none; max-width: 400px; margin: 0 auto; }
    .hours-list li { padding: 12px 0; border-bottom: 1px solid rgba(107,66,38,.15); color: var(--text-muted); font-size: .95rem; }

    /* GOOGLE REVIEWS */
    .google-reviews { background: var(--accent); padding: 16px 0; }
    .review-badge { display: flex; align-items: center; justify-content: center; gap: 12px; flex-wrap: wrap; }
    .review-badge .star, .review-badge .star.filled { color: var(--bg); font-size: 1.2rem; }
    .review-text { color: var(--bg); font-weight: 700; font-size: .95rem; }

    /* PROMO */
    .promo-banner { background: white; padding: 20px 0; border-top: 3px solid var(--accent); border-bottom: 3px solid var(--accent); }
    .promo-inner { display: flex; align-items: center; gap: 20px; flex-wrap: wrap; justify-content: center; }
    .promo-badge { background: var(--accent); color: var(--bg); font-family: var(--font-heading); padding: 8px 20px; font-size: 1.1rem; border-radius: 6px; }
    .promo-content strong { display: block; margin-bottom: 2px; }
    .promo-content span { color: var(--text-muted); font-size: .9rem; }

    /* CONTACT - split with accent bar */
    .contact-split { display: grid; grid-template-columns: 8px 1fr 1fr; gap: 0; align-items: stretch; }
    .contact-accent { background: var(--accent); border-radius: 8px 0 0 8px; }
    .contact-info { display: flex; flex-direction: column; gap: 20px; background: white; padding: 40px 32px; }
    .contact-item { display: flex; align-items: center; gap: 12px; color: var(--text-muted); }
    .contact-item .icon-char { color: var(--accent); flex-shrink: 0; font-size: 20px; line-height: 1; }
    .contact-item a { color: var(--text); }
    .map-link { display: inline-block; margin-top: 8px; font-weight: 600; text-transform: uppercase; font-size: .85rem; letter-spacing: 1px; }
    .contact-form { display: flex; flex-direction: column; gap: 16px; background: var(--bg); padding: 40px 32px; border-radius: 0 8px 8px 0; }
    .contact-form input, .contact-form textarea { background: white; border: 2px solid rgba(107,66,38,.3); color: var(--text); padding: 14px 16px; font-family: var(--font-body); font-size: .95rem; outline: none; border-radius: 8px; transition: border-color .2s; }
    .contact-form input:focus, .contact-form textarea:focus { border-color: var(--accent); }
    .contact-form button { padding: 16px; background: var(--accent); color: var(--bg); font-family: var(--font-heading); font-size: 1.05rem; letter-spacing: 1px; border: none; border-radius: 8px; cursor: pointer; transition: transform .2s; }
    .contact-form button:hover { transform: translateY(-2px); }

    /* FOOTER */
    .footer { background: var(--accent); padding: 40px 0; text-align: center; }
    .footer p { color: rgba(255,248,231,.7); font-size: .85rem; }

    /* RESPONSIVE */
    @media (max-width: 768px) {
      .nav-links { display: none; }
      .nav-links.show { display: flex; flex-direction: column; position: absolute; top: 100%; left: 0; right: 0; background: rgba(255,248,231,.98); padding: 16px 24px; gap: 12px; border-bottom: 4px solid var(--accent); }
      .nav-toggle { display: block; }
      .services-masonry { column-count: 1; }
      .about-grid { grid-template-columns: 1fr; }
      .gallery-grid { grid-template-columns: repeat(2, 1fr); }
      .gallery-frame.frame-overlap { margin-top: 0; }
      .contact-split { grid-template-columns: 8px 1fr; }
      .contact-form { border-radius: 0; }
      .testimonial-card { min-width: 260px; }
      .price-row { flex-direction: column; gap: 8px; }
      .hero-accent-bar { width: 40px; }
      .hero-content { padding-left: 60px; }
    }
    @media (max-width: 480px) {
      .gallery-grid { grid-template-columns: 1fr; }
      .hero-content { padding: 100px 16px 60px 60px; }
    }
  </style>
</head>
<body>
  <nav class="navbar">
    <div class="nav-inner">
      <a href="#" class="nav-brand">${safeName}</a>
      <button class="nav-toggle" aria-label="Toggle menu" onclick="this.parentElement.querySelector('.nav-links').classList.toggle('show')">
        <span></span><span></span><span></span>
      </button>
      <ul class="nav-links">
        ${navLinks.map(l => `<li><a href="${l.href}">${l.label}</a></li>`).join('')}
      </ul>
    </div>
  </nav>

  <section class="hero">
    ${heroImage ? `<div class="hero-bg" style="background-image:url('${escapeAttr(heroImage)}')"></div>` : ''}
    <div class="hero-overlay"></div>
    <div class="hero-accent-bar"></div>
    <div class="hero-content">
      <h1>${safeHeadline}</h1>
      <p class="hero-sub">${truncateAtSentence(safeAbout)}</p>
      ${enabledFeatures.has('contact-form') ? `<a href="#contact" class="hero-cta">Get Started</a>` : ''}
    </div>
  </section>

  ${sections.join('\n')}

  <footer class="footer">
    <div class="container">
      <p>${safeName}</p>
    </div>
  </footer>
</body>
</html>`;
  }
};
